import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import type { StaffEmailRow } from '@ps/api-client/staff';
import { Card, CardTitle } from '@ps/ui-kit/components/card';
import { Pill, type Tone } from '@ps/ui-kit/components/pill';
import { Empty, ErrorState, Loading } from '@ps/ui-kit/components/states';
import { Table } from '@ps/ui-kit/components/table';
import { dateTime } from '@ps/ui-kit/lib/format';

// Email history (email specification §5 step 5, §9 check 10; ADR-104): every email on a customer record (admin) and a
// payment record (console), newest first: which email, its subject, the masked recipient, what happened and, when it
// was not delivered, why. Read-only; no body is stored.

/** The specification's name for each email (§4), and our account emails. */
export const EMAIL_NAMES: Record<string, string> = {
  APPLICATION_STARTED: 'O1 Application started', IDENTITY_REMINDER: 'O2 Identity verification reminder', APPLICATION_SUBMITTED: 'O3 Application under review',
  RFI_RAISED: 'O4 Additional documents required', RFI_REMINDER: 'O5 Documents reminder', APPLICATION_APPROVED: 'O6 Application approved',
  ACCOUNT_ACTIVE: 'O7 Account active', APPLICATION_DECLINED: 'O8 Application unsuccessful',
  CONVERSION_COMPLETED: 'T1 Conversion completed', PAYMENT_SUBMITTED: 'T2 Payment submitted', PAYMENT_INFO_REQUESTED: 'T3 Information required for payment',
  PAYMENT_COMPLETED: 'T4 Payment completed', PAYMENT_FAILED: 'T5 Payment could not be completed', FUNDS_RECEIVED: 'T6 Funds received',
  BENEFICIARY_ADDED: 'T7 New beneficiary added', OPS_ALERT: 'Tech alert',
  VERIFY_EMAIL: 'Confirm email address', ACCOUNT_EXISTS: 'Account already exists', FINISH_SETUP: 'Finish setting up', PASSWORD_RESET: 'Password reset',
};

const STATUS: Record<StaffEmailRow['status'], { label: string; tone: Tone }> = {
  SENT: { label: 'Sent', tone: 'ok' }, PREVIEW: { label: 'Preview', tone: 'info' }, FAILED: { label: 'Failed', tone: 'bad' },
  BLOCKED: { label: 'Blocked', tone: 'bad' }, SKIPPED: { label: 'Skipped', tone: 'neu' },
};

export function EmailHistory({ queryKey, load }: { queryKey: readonly unknown[]; load: () => Promise<{ items: StaffEmailRow[] }> }) {
  const q = useQuery({ queryKey: [...queryKey, 'emails'], queryFn: load });
  return (
    <Card>
      <CardTitle>Emails</CardTitle>
      {q.isPending ? <Loading label="Loading emails…" />
        : q.error ? <ErrorState message="We couldn't load the email history." onRetry={() => void q.refetch()} />
        : !q.data.items.length ? <Empty title="No emails yet" />
        : (
          <div className="overflow-x-auto">
            <Table bare tableClassName="[overflow-wrap:anywhere]">
              <thead><tr><th>When</th><th>Email</th><th>To</th><th>Status</th></tr></thead>
              <tbody>
                {q.data.items.map(e => (
                  <tr key={e.id}>
                    <td className="whitespace-nowrap text-ink-3">{dateTime(e.at)}</td>
                    <td><span className="font-semibold text-ink">{EMAIL_NAMES[e.template] ?? e.template}</span>
                      {e.subject && <span className="block text-xs text-ink-3">{e.subject}</span>}</td>
                    <td className="font-mono text-xs">{e.recipient ?? '—'}</td>
                    <td><Pill tone={STATUS[e.status].tone} upper>{STATUS[e.status].label}</Pill>
                      {e.reason && <span className="block text-xs text-ink-3">{e.reason}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
    </Card>
  );
}
